import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, Globe, Lock, Check, AlertCircle, Book, Award, CircleDot, Link, GripHorizontal, Plus, Trash2, ImagePlus } from 'lucide-react';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import '../../styles/pages/User.scss';
import { TOPICS, EDUCATIONAL_LEVELS } from '../../utils/materialTaxonomy';
import ImageCropModal, { cropStyle } from '../common/ImageCropModal';

const SPECIALTIES = ['General', 'Organic', 'Inorganic', 'Genetics', 'Thermodynamics', 'Calculus', 'Web Development', 'Macroeconomics', 'Cardiology', 'Other'];
const LANGUAGES = ['English', 'Azerbaijani', 'Turkish', 'Russian', 'German', 'Other'];

const CATEGORIES = [
    { id: 'Book', label: 'Book', hint: 'Comprehensive textbook or manual.', icon: Book },
    { id: 'Note', label: 'Note', hint: 'Lecture notes or personal study guides.', icon: FileText },
    { id: 'Exercise', label: 'Exercise', hint: 'Practice problems and worksheets.', icon: CircleDot },
    { id: 'Quiz', label: 'Quiz', hint: 'Test preparations and mock exams.', icon: Award },
];

const VISIBILITY_OPTIONS = [
    { id: 'Public', label: 'Public', hint: 'Anyone can search for and view this note.', icon: Globe },
    { id: 'Private', label: 'Private', hint: 'Only you can see and access this note.', icon: Lock },
    { id: 'Unlisted', label: 'Unlisted', hint: 'Only people with the link can view this note.', icon: Link },
];

// ReorderItem defined outside to prevent remounts during drag
const ReorderItem = ({ item, idx, onRemove }) => {
    const dragControls = useDragControls();

    return (
        <Reorder.Item 
            value={item}
            className="staged-card-mini"
            as="li"
            dragListener={false}
            dragControls={dragControls}
            dragElastic={0.1}
            dragMomentum={false}
            whileDrag={{ 
                scale: 1.05, 
                boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                zIndex: 100
            }}
            style={{ userSelect: 'none' }}
        >
            <div className="order-badge">{idx + 1}</div>
            
            {/* Drag Handle */}
            <div 
                className="drag-handle"
                onPointerDown={(e) => dragControls.start(e)}
            >
                <GripHorizontal size={16} />
            </div>

            <div className="preview-box">
                {item.previewUrl ? (
                    <img src={item.previewUrl} alt="preview" />
                ) : (
                    <div className="pdf-preview-icon">
                        <FileText size={40} />
                    </div>
                )}
            </div>
            <span className="file-name">{item.name}</span>
            
            <button className="remove-staged-btn" onClick={() => onRemove(item.id)}>
                <X size={14} />
            </button>
        </Reorder.Item>
    );
};

const UploadPanel = ({ stagedFiles, setStagedFiles, setIsOpen, token, onUploadSuccess, uploadType, setUploadType }) => {
    const [metadata, setMetadata] = useState({
        title: stagedFiles.length === 1 ? stagedFiles[0].name.replace(/\.[^/.]+$/, "") : "",
        description: "",
        category: 'Note',
        topic: 'Chemistry',
        specialty: 'General',
        language: 'English',
        educationalLevel: 'University',
        university: '',
        author: '',
        visibility: 'Public',
        isPaid: false,
    });
    const [cover, setCover] = useState({ url: '', crop: null });
    const [coverModalOpen, setCoverModalOpen] = useState(false);
    const [sources, setSources] = useState([]);
    const [latexCode, setLatexCode] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [isDetailedOpen, setIsDetailedOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        // Reset specific LaTeX state when switching mode internally
        if (uploadType !== 'latex') {
            setLatexCode('');
        }
        setError(null);
    }, [uploadType]);

    const set = (key, val) => setMetadata(prev => ({ ...prev, [key]: val }));

    const handleRemoveFile = (id) => {
        const item = stagedFiles.find(f => f.id === id);
        if (item && item.previewUrl) {
            URL.revokeObjectURL(item.previewUrl);
        }
        const updated = stagedFiles.filter(f => f.id !== id);
        setStagedFiles(updated);
        if (updated.length === 0) setIsOpen(false);
    };

    const handleUpload = async () => {
        if (!metadata.topic) { setError("Please select a topic."); return; }
        if (!agreed) { setError("You must agree to the Terms of Service."); return; }

        setUploading(true);
        setError(null);

        const formData = new FormData();
        formData.append('uploadType', uploadType);
        if (uploadType === 'latex') {
            formData.append('latexCode', latexCode || '');
        }
        
        stagedFiles.forEach(f => formData.append('files', f.file));
        Object.entries(metadata).forEach(([key, value]) => formData.append(key, value));

        if (cover.url) {
            formData.append('coverUrl', cover.url);
            formData.append('coverCrop', JSON.stringify(cover.crop || {}));
        }

        if (sources.length > 0) {
            formData.append('sources', JSON.stringify(sources));
        }

        try {
            const res = await fetch('/api/upload', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            const data = await res.json();
            if (res.ok) {
                setSuccess(true);
                setTimeout(() => {
                    setStagedFiles([]);
                    setIsOpen(false);
                    if (onUploadSuccess) onUploadSuccess();
                }, 1500);
            } else {
                setError(data.message || data.error || "Upload failed. Please try again.");
            }
        } catch (err) {
            console.error(err);
            setError("Network error. Check your connection.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="upload-panel-overlay">
            {/* The overlay itself isn't animated — it has a backdrop-filter blur,
                and animating opacity on a blurred full-screen layer forces an
                expensive re-blur every frame. Only the panel card animates. */}
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 16 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={`upload-panel ${stagedFiles.length > 3 ? 'expanded-panel' : ''}`}
            >
                {/* ── Header ── */}
                <div className="panel-header" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'flex-start', paddingBottom: '1rem' }}>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2>Upload Document</h2>
                        <button className="close-btn" onClick={() => setIsOpen(false)}>
                            <X size={20} />
                        </button>
                    </div>
                    {setUploadType && (
                        <div style={{ display: 'flex', gap: '0.5rem', background: 'rgba(0,0,0,0.2)', padding: '4px', borderRadius: '8px' }}>
                            <button 
                                onClick={() => {
                                    if (uploadType !== 'material') {
                                        setStagedFiles([]);
                                        setUploadType('material');
                                    }
                                }}
                                style={{
                                    padding: '6px 12px', 
                                    borderRadius: '6px', 
                                    background: uploadType === 'material' ? 'rgba(255,255,255,0.1)' : 'transparent',
                                    color: uploadType === 'material' ? '#fff' : '#888',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s'
                                }}
                            >
                                PDF / Images
                            </button>
                            <button 
                                onClick={() => {
                                    if (uploadType !== 'latex') {
                                        setStagedFiles([]);
                                        setUploadType('latex');
                                    }
                                }}
                                style={{
                                    padding: '6px 12px', 
                                    borderRadius: '6px', 
                                    background: uploadType === 'latex' ? 'rgba(255,255,255,0.1)' : 'transparent',
                                    color: uploadType === 'latex' ? '#fff' : '#888',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s'
                                }}
                            >
                                LaTeX Code
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Scrollable body ── */}
                <div className="upload-content-vertical">
                    {error && (
                        <div className="form-feedback error">
                            <AlertCircle size={16} /> {error}
                        </div>
                    )}
                    {success && (
                        <div className="form-feedback success">
                            <Check size={16} /> Upload successful! Redirecting...
                        </div>
                    )}

                    {/* Staged files / LaTeX Editor */}
                    <div className="files-section-top">
                        <div className="files-header">
                            <h3>{uploadType === 'latex' ? 'LaTeX Source' : 'Staged PDF / Images'} ({stagedFiles.length})</h3>
                            <label className="add-files-btn">
                                <Upload size={14} /> {uploadType === 'latex' ? 'Add .tex File' : 'Add more'}
                                <input
                                    type="file"
                                    multiple={uploadType !== 'latex'}
                                    accept={uploadType === 'latex' ? '.tex,text/plain,text/x-tex' : '.pdf,application/pdf,image/*'}
                                    className="hidden"
                                    onChange={(e) => {
                                        const filesArray = Array.from(e.target.files);
                                        const filtered = uploadType === 'latex' 
                                            ? filesArray.filter(f => 
                                                f.name.trim().toLowerCase().endsWith('.tex') || 
                                                f.type.includes('tex') ||
                                                f.type === 'text/plain'
                                            )
                                            : filesArray;

                                        if (uploadType === 'latex' && filtered.length === 0 && filesArray.length > 0) {
                                            setError('Only .tex files are allowed in LaTeX mode.');
                                            return;
                                        }

                                        const newStagedFiles = filtered.map(file => {
                                            const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
                                            return {
                                                id: `file-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`,
                                                file,
                                                name: file.name,
                                                previewUrl
                                            };
                                        });
                                        // For LaTeX, typically we want only one main file if multiple are selected
                                        if (uploadType === 'latex') {
                                            setStagedFiles(newStagedFiles.slice(0, 1));
                                        } else {
                                            setStagedFiles(prev => [...prev, ...newStagedFiles]);
                                        }
                                    }}
                                />
                            </label>
                        </div>

                        {uploadType === 'latex' ? (
                            <div className="latex-editor-wrapper">
                                <textarea
                                    className="latex-editor-simple"
                                    placeholder="Write or paste your LaTeX code here... \documentclass{article} \begin{document} ..."
                                    value={latexCode}
                                    onChange={(e) => setLatexCode(e.target.value)}
                                />
                                {stagedFiles.length > 0 && (
                                    <div className="staged-tex-preview">
                                        <p className="tiny-label">Loaded File:</p>
                                        <div className="tex-file-pill">
                                            <FileText size={14} />
                                            <span>{stagedFiles[0].name}</span>
                                            <button onClick={() => setStagedFiles([])}><X size={12} /></button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Reorder.Group 
                                axis="x" 
                                values={stagedFiles} 
                                onReorder={setStagedFiles}
                                className="staged-grid-horizontal"
                                as="ul"
                            >
                                {stagedFiles.map((item, idx) => (
                                    <ReorderItem 
                                        key={item.id}
                                        item={item} 
                                        idx={idx} 
                                        onRemove={handleRemoveFile} 
                                    />
                                ))}
                            </Reorder.Group>
                        )}
                    </div>

                    {/* ── Metadata area ── */}
                    <div className="metadata-section-bottom">
                        {/* Title */}
                        <div className="input-wrapper full-width">
                            <label>TITLE <span className="req">*</span></label>
                            <input
                                type="text"
                                className="dark-input"
                                placeholder="e.g., Intro to Organic Chemistry"
                                value={metadata.title}
                                onChange={(e) => set('title', e.target.value)}
                            />
                        </div>

                        {/* Cover image */}
                        <div className="input-wrapper full-width">
                            <label>COVER IMAGE</label>
                            {cover.url ? (
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                                    <div style={{ position: 'relative', width: '160px', aspectRatio: '16 / 9', borderRadius: '8px', overflow: 'hidden', background: 'rgba(255,255,255,0.05)', flexShrink: 0 }}>
                                        <img src={cover.url} alt="Cover preview" style={{ ...cropStyle(cover.crop), position: 'absolute', inset: 0 }} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'center' }}>
                                        <button
                                            type="button"
                                            onClick={() => setCoverModalOpen(true)}
                                            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#e5e7eb', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                                        >
                                            Adjust
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCover({ url: '', crop: null })}
                                            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#9ca3af', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setCoverModalOpen(true)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '12px', border: '1px dashed rgba(255,255,255,0.15)', borderRadius: '8px', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                                >
                                    <ImagePlus size={16} /> Add a cover image link (optional)
                                </button>
                            )}
                        </div>

                        {/* Topic + Specialty row */}
                        <div className="form-grid-two">
                            <div className="input-wrapper">
                                <label>TOPIC <span className="req">*</span></label>
                                <select
                                    className="dark-select-input"
                                    value={metadata.topic}
                                    onChange={(e) => set('topic', e.target.value)}
                                >
                                    {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="input-wrapper">
                                <label>SPECIALTY <span className="req">*</span></label>
                                <select
                                    className="dark-select-input"
                                    value={metadata.specialty}
                                    onChange={(e) => set('specialty', e.target.value)}
                                >
                                    {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Language + Level */}
                        <div className="form-grid-two">
                            <div className="input-wrapper">
                                <label>LANGUAGE <span className="req">*</span></label>
                                <select
                                    className="dark-select-input"
                                    value={metadata.language}
                                    onChange={(e) => set('language', e.target.value)}
                                >
                                    {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </div>
                            <div className="input-wrapper">
                                <label>EDUCATIONAL LEVEL <span className="req">*</span></label>
                                <select
                                    className="dark-select-input"
                                    value={metadata.educationalLevel}
                                    onChange={(e) => set('educationalLevel', e.target.value)}
                                >
                                    {EDUCATIONAL_LEVELS.map(lev => <option key={lev} value={lev}>{lev}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Category + Visibility cards */}
                        <div className="redesign-container">
                            {/* Category */}
                            <div>
                                <span className="section-title-muted">CATEGORY</span>
                                <div className="category-options-grid">
                                    {CATEGORIES.map((cat) => (
                                        <div
                                            key={cat.id}
                                            className={`category-card-item ${metadata.category === cat.id ? 'active' : ''}`}
                                            onClick={() => set('category', cat.id)}
                                        >
                                            <div className="card-icon"><cat.icon size={22} /></div>
                                            <div className="card-content">
                                                <span className="card-title">{cat.label}</span>
                                                <p className="card-hint">{cat.hint}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Visibility */}
                            <div>
                                <span className="section-title-muted">VISIBILITY</span>
                                <div className="visibility-grid-three">
                                    {VISIBILITY_OPTIONS.map((opt) => (
                                        <div
                                            key={opt.id}
                                            className={`visibility-card-item ${metadata.visibility === opt.id ? 'active' : ''}`}
                                            onClick={() => set('visibility', opt.id)}
                                        >
                                            <div className="card-icon"><opt.icon size={20} /></div>
                                            <div className="card-content">
                                                <span className="card-title">{opt.label}</span>
                                                <p className="card-hint">{opt.hint}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Terms */}
                            <div className="redesign-terms">
                                <label className="checkbox-container">
                                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                                    <div className="checkmark"></div>
                                    <span className="label-text">
                                        I agree to the <a href="#terms">Terms of Service</a>
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* ── Detailed Data toggle ── */}
                        <div className="redesign-toggle">
                            <div className="advanced-toggle-pill" onClick={() => setIsDetailedOpen(!isDetailedOpen)}>
                                <span>Detailed Data</span>
                                <div className={`toggle-switch-pill ${isDetailedOpen ? 'active' : ''}`}></div>
                            </div>
                        </div>

                        {/* ── Expanded detailed fields ── */}
                        <AnimatePresence>
                            {isDetailedOpen && (
                                <motion.div
                                    key="detailed"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    style={{ overflow: 'hidden' }}
                                    className="redesignated-detailed"
                                >
                                    {/* Monetization */}
                                    <div className="monetization-row-advanced">
                                        <div
                                            className={`monetization-control ${metadata.isPaid ? 'is-paid' : ''}`}
                                            onClick={() => set('isPaid', !metadata.isPaid)}
                                        >
                                            <div className="monetization-info">
                                                <span>Earn money from this note?</span>
                                                <p className="sub-label">
                                                    {metadata.isPaid
                                                        ? 'This note will be available for purchase.'
                                                        : 'This note will be shared with the community for free.'}
                                                </p>
                                            </div>
                                            <div className="status-label">{metadata.isPaid ? 'Paid' : 'Free'}</div>
                                            <div className={`toggle-switch mini ${metadata.isPaid ? 'active' : ''}`}></div>
                                        </div>
                                    </div>

                                    {/* University + Author */}
                                    <div className="form-grid-two">
                                        <div className="input-wrapper">
                                            <label>UNIVERSITY / INSTITUTION</label>
                                            <input
                                                type="text"
                                                className="dark-input"
                                                placeholder="e.g., ADA University"
                                                value={metadata.university}
                                                onChange={(e) => set('university', e.target.value)}
                                            />
                                        </div>
                                        <div className="input-wrapper">
                                            <label>AUTHOR / PROFESSOR</label>
                                            <input
                                                type="text"
                                                className="dark-input"
                                                placeholder="e.g., Dr. Ismayilov"
                                                value={metadata.author}
                                                onChange={(e) => set('author', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="input-wrapper full-width">
                                        <label>DESCRIPTION</label>
                                        <textarea
                                            className="dark-input"
                                            placeholder="What is this note about? Provide some details to help others find it..."
                                            value={metadata.description}
                                            onChange={(e) => set('description', e.target.value)}
                                        />
                                    </div>

                                    {/* Sources Section */}
                                    <div className="input-wrapper full-width" style={{ marginTop: '1rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <label style={{ margin: 0 }}>SOURCES</label>
                                            <button 
                                                onClick={() => setSources([...sources, { title: '', url: '', type: 'Web' }])}
                                                style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', textTransform: 'uppercase', fontWeight: 600 }}
                                            >
                                                <Plus size={12} /> Add Source
                                            </button>
                                        </div>
                                        {sources.length === 0 ? (
                                            <div style={{ padding: '12px', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '8px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
                                                No sources added yet. Click "Add Source" to include related links.
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {sources.map((src, index) => (
                                                    <div key={index} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.5fr) 100px 32px', gap: '8px', alignItems: 'center' }}>
                                                        <input 
                                                            className="dark-input" 
                                                            placeholder="Title" 
                                                            value={src.title} 
                                                            onChange={(e) => {
                                                                const newSrc = [...sources];
                                                                newSrc[index].title = e.target.value;
                                                                setSources(newSrc);
                                                            }} 
                                                        />
                                                        <input 
                                                            className="dark-input" 
                                                            placeholder="URL" 
                                                            value={src.url} 
                                                            onChange={(e) => {
                                                                const newSrc = [...sources];
                                                                newSrc[index].url = e.target.value;
                                                                setSources(newSrc);
                                                            }} 
                                                        />
                                                        <select 
                                                            className="dark-select-input" 
                                                            value={src.type}
                                                            onChange={(e) => {
                                                                const newSrc = [...sources];
                                                                newSrc[index].type = e.target.value;
                                                                setSources(newSrc);
                                                            }}
                                                        >
                                                            <option value="Web">Web</option>
                                                            <option value="YouTube">YouTube</option>
                                                            <option value="PDF">PDF</option>
                                                            <option value="Google Docs">Docs</option>
                                                        </select>
                                                        <button 
                                                            onClick={() => setSources(sources.filter((_, i) => i !== index))}
                                                            style={{ background: 'transparent', border: 'none', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '4px' }}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="redesign-footer">
                    <button className="cancel-btn" onClick={() => setIsOpen(false)}>Cancel</button>
                    <button
                        className="submit-upload-btn"
                        onClick={handleUpload}
                        disabled={uploading || (uploadType === 'latex' ? (!latexCode && stagedFiles.length === 0) : stagedFiles.length === 0)}
                    >
                        {uploading ? "Uploading..." : (uploadType === 'latex' ? "Compile & Upload" : "Upload Notes")}
                    </button>
                </div>

            </motion.div>

            <ImageCropModal
                isOpen={coverModalOpen}
                title="Cover image"
                initialUrl={cover.url}
                initialCrop={cover.crop}
                onCancel={() => setCoverModalOpen(false)}
                onSave={({ url, crop }) => {
                    setCover({ url, crop: url ? crop : null });
                    setCoverModalOpen(false);
                }}
            />
        </div>
    );
};

export default UploadPanel;
